<template>
  <v-snackbar
    v-model="notificationStore.snackbar"
    :timeout="notificationStore.timeout"
    location="top"
    elevation="8"
    transition="slide-y-transition"
    rounded="lg"
    min-width="380px"
    max-width="600px"
    class="notification-snackbar"
    :class="`notification-${notificationStore.type}`"
  >
    <div class="notification-content">
      <div class="d-flex align-start">
        <!-- Icon Section -->
        <div class="notification-icon-wrapper">
          <v-icon 
            :color="colors.text"
            size="22"
            class="notification-icon"
          >
            {{ colors.icon }}
          </v-icon>
        </div>

        <!-- Content Section -->
        <div class="notification-text-section">
          <div class="notification-message" :style="{ color: colors.text }">
            {{ notificationStore.message }}
          </div>
          
          <!-- Action Button (if provided) -->
          <v-btn
            v-if="notificationStore.actionText"
            @click="notificationStore.executeAction"
            variant="outlined"
            size="small"
            class="notification-action-btn mt-2"
            :style="{ 
              borderColor: colors.text, 
              color: colors.text 
            }"
          >
            {{ notificationStore.actionText }}
          </v-btn>
        </div>

        <!-- Close Button -->
        <v-btn 
          icon 
          size="small"
          @click="notificationStore.close"
          variant="text"
          class="notification-close-btn"
          :style="{ color: colors.text }"
          aria-label="Close notification"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Progress indicator for timed notifications -->
      <div 
        v-if="notificationStore.timeout > 0"
        class="notification-progress"
        :class="`progress-${notificationStore.type}`"
      ></div>
    </div>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useNotificationStore } from "@/stores/notificationStore";

const notificationStore = useNotificationStore();

const colors = computed(() => {
  return notificationStore.getNotificationColors(notificationStore.type);
});
</script>

<style scoped>
/* ===========================================
   PROFESSIONAL NOTIFICATION SYSTEM STYLES
   Concerton Budget Management Application
   =========================================== */

/* Main Notification Container */
.notification-content {
  padding: 0;
  position: relative;
  width: 100%;
}

/* Layout Structure */
.notification-icon-wrapper {
  margin-right: 16px;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.notification-text-section {
  flex-grow: 1;
  min-width: 0; /* Allows text to wrap properly */
}

.notification-message {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0;
  word-wrap: break-word;
  hyphens: auto;
}

/* Icon Styling */
.notification-icon {
  opacity: 0.95;
  transition: opacity 0.2s ease;
}

/* Action Button Styling */
.notification-action-btn {
  margin-top: 8px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.notification-action-btn:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

/* Close Button Styling */
.notification-close-btn {
  margin-left: 12px;
  opacity: 0.8;
  transition: all 0.2s ease;
}

.notification-close-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Progress Indicator */
.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  animation: progress-countdown linear;
  animation-duration: var(--timeout-duration, 5s);
  border-radius: 0 0 12px 12px;
}

.progress-success { background: #4CAF50; }
.progress-error { background: #F44336; }
.progress-warning { background: #FF9800; }
.progress-info { background: #2196F3; }

@keyframes progress-countdown {
  from { width: 100%; }
  to { width: 0%; }
}

/* ===========================================
   VUETIFY SNACKBAR OVERRIDES
   =========================================== */

:deep(.v-snackbar__wrapper) {
  border-radius: 12px !important;
  backdrop-filter: blur(12px) saturate(180%);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.1),
    0 1px 2px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: visible;
}

:deep(.v-snackbar__content) {
  padding: 16px 20px !important;
  font-size: 15px !important;
}

/* ===========================================
   NOTIFICATION TYPE SPECIFIC STYLING
   =========================================== */

/* Success Notifications */
:deep(.notification-success .v-snackbar__wrapper) {
  background: linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%) !important;
  border-left: 4px solid #4CAF50;
}

/* Error Notifications */
:deep(.notification-error .v-snackbar__wrapper) {
  background: linear-gradient(135deg, #B71C1C 0%, #D32F2F 100%) !important;
  border-left: 4px solid #F44336;
}

/* Warning Notifications */  
:deep(.notification-warning .v-snackbar__wrapper) {
  background: linear-gradient(135deg, #E65100 0%, #F57C00 100%) !important;
  border-left: 4px solid #FF9800;
}

/* Info Notifications */
:deep(.notification-info .v-snackbar__wrapper) {
  background: linear-gradient(135deg, #0D47A1 0%, #1976D2 100%) !important;
  border-left: 4px solid #2196F3;
}

/* ===========================================
   ANIMATIONS & TRANSITIONS
   =========================================== */

/* Enhanced entrance animation */
:deep(.v-snackbar.v-snackbar--active) {
  animation: notification-slide-in 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes notification-slide-in {
  0% { 
    transform: translateY(-100%) scale(0.95); 
    opacity: 0; 
  }
  50% { 
    transform: translateY(8px) scale(1.02); 
    opacity: 0.9; 
  }
  100% { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
}

/* Smooth transitions for all interactive elements */
* {
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* ===========================================
   RESPONSIVE DESIGN
   =========================================== */

/* Tablet and Mobile Adjustments */
@media (max-width: 768px) {
  :deep(.v-snackbar__wrapper) {
    min-width: 320px !important;
    max-width: calc(100vw - 32px) !important;
    margin: 0 16px;
  }
  
  .notification-message {
    font-size: 14px;
  }
  
  :deep(.v-snackbar__content) {
    padding: 14px 16px !important;
  }
}

/* ===========================================
   ACCESSIBILITY ENHANCEMENTS
   =========================================== */

/* Focus indicators for keyboard navigation */
.notification-action-btn:focus,
.notification-close-btn:focus {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  :deep(.v-snackbar__wrapper) {
    border-width: 2px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
  }
  
  .notification-message {
    font-weight: 600;
  }
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  *,
  :deep(.v-snackbar.v-snackbar--active) {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Print styles (hide notifications when printing) */
@media print {
  :deep(.v-snackbar) {
    display: none !important;
  }
}
</style>
